/**
 * Recipe API Client
 * Handles communication with the Laravel backend for recipe management
 */

export class RecipeAPI {
  /** @type {string} */
  #baseUrl;

  /** @type {string} */
  #locale;

  /** @type {Map<string, any>} */
  #cache = new Map();

  /** @type {number} */
  #cacheTimeout = 5 * 60 * 1000; // 5 minutes

  /**
   * @param {string} baseUrl - The base URL of the Laravel API
   * @param {string} locale - The current locale (cs, sk, hu, de, ru)
   */
  constructor(baseUrl, locale = 'cs') {
    this.#baseUrl = baseUrl.replace(/\/$/, ''); // Remove trailing slash
    this.#locale = locale;
  }

  /**
   * Get cached data if valid
   * @param {string} key
   * @returns {any|null}
   */
  #getFromCache(key) {
    const cached = this.#cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.#cacheTimeout) {
      return cached.data;
    }
    this.#cache.delete(key);
    return null;
  }

  /**
   * Store data in cache
   * @param {string} key
   * @param {any} data
   */
  #setCache(key, data) {
    this.#cache.set(key, {
      data,
      timestamp: Date.now(),
    });
  }

  /**
   * Make an API request
   * @param {string} endpoint
   * @param {RequestInit} options
   * @returns {Promise<any>}
   */
  async #request(endpoint, options = {}) {
    const url = `${this.#baseUrl}${endpoint}`;

    const defaultHeaders = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    };

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);

    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          ...defaultHeaders,
          ...options.headers,
        },
        signal: controller.signal,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // Laravel validation errors come as { error: "...", messages: { field: ["msg"] } }
        const message =
          errorData.message ||
          (errorData.messages && Object.values(errorData.messages).flat().join(', ')) ||
          `HTTP ${response.status}`;
        throw new Error(message);
      }

      return response.json();
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('Request timed out');
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Get list of problems with essence mappings
   * @returns {Promise<Array<{id: number, text: string, essences: number[]}>>}
   */
  async getProblems() {
    const cacheKey = `problems_${this.#locale}`;
    const cached = this.#getFromCache(cacheKey);
    if (cached) return cached;

    const response = await this.#request(`/problems?locale=${this.#locale}`);
    const data = response.data || response;
    this.#setCache(cacheKey, data);
    return data;
  }

  /**
   * Get list of essences
   * @returns {Promise<Array<{id: number, name_local: string, name_english: string}>>}
   */
  async getEssences() {
    const cacheKey = `essences_${this.#locale}`;
    const cached = this.#getFromCache(cacheKey);
    if (cached) return cached;

    const response = await this.#request(`/essences?locale=${this.#locale}`);
    const data = response.data || response;
    this.#setCache(cacheKey, data);
    return data;
  }

  /**
   * Search for existing recipes by email
   * @param {string} email
   * @returns {Promise<Array<{id: number, name: string, created_at: string, is_approved: boolean, problems_summary: string}>>}
   */
  async searchRecipes(email) {
    if (!email || !email.includes('@')) {
      return [];
    }

    const response = await this.#request(
      `/recipes?email=${encodeURIComponent(email)}&locale=${this.#locale}`
    );
    return response.data || response;
  }

  /**
   * Get single recipe by ID.
   *
   * The endpoint is public, so it requires the customer's e-mail as proof of
   * ownership — a mismatch answers 404, the same as a missing recipe.
   *
   * @param {number} id
   * @param {string} customerEmail - Must match the recipe's customer_email
   * @returns {Promise<object>}
   */
  async getRecipe(id, customerEmail) {
    const params = new URLSearchParams({ locale: this.#locale, email: customerEmail });
    const response = await this.#request(`/recipes/${id}?${params}`);
    return response.data || response;
  }

  /**
   * Create a new recipe
   * @param {object} data
   * @param {string} data.name - Recipe name
   * @param {string} data.customer_email - Customer email
   * @param {number} data.shopify_product_id - Shopify product ID
   * @param {number} [data.shopify_customer_id] - Shopify customer ID
   * @param {string} [data.health_issues] - Health issues description
   * @param {string} [data.medications] - Medications list
   * @param {string} [data.medications_purpose] - Medications purpose
   * @param {'list'|'text'} data.creation_method - How the recipe was created
   * @param {number[]} [data.selected_problems] - Selected problem IDs (for 'list' method)
   * @param {string[]} [data.custom_problems] - Custom problem texts (for 'text' method)
   * @param {string} [data.desired_outcome] - Desired outcome description
   * @param {string} [data.child_age] - Child age (optional, free text)
   * @returns {Promise<{id: number, name: string, created_at: string}>}
   */
  async createRecipe(data) {
    const payload = {
      ...data,
      locale: this.#locale,
    };

    const response = await this.#request('/recipes', {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    return response.data || response;
  }

  /**
   * Set the current locale
   * @param {string} locale
   */
  setLocale(locale) {
    this.#locale = locale;
  }

  /**
   * Get the current locale
   * @returns {string}
   */
  getLocale() {
    return this.#locale;
  }

  /**
   * Clear the cache
   */
  clearCache() {
    this.#cache.clear();
  }

  /**
   * Check if the API is configured (has a valid URL)
   * @returns {boolean}
   */
  isConfigured() {
    return Boolean(this.#baseUrl && this.#baseUrl.length > 0);
  }
}

// Singleton instance for global access
let apiInstance = null;

/**
 * Get or create the RecipeAPI instance
 * @param {string} [baseUrl]
 * @param {string} [locale]
 * @returns {RecipeAPI}
 */
export function getRecipeAPI(baseUrl, locale) {
  if (!apiInstance && baseUrl) {
    apiInstance = new RecipeAPI(baseUrl, locale);
  } else if (apiInstance && locale) {
    apiInstance.setLocale(locale);
  }
  return apiInstance;
}
